/**
 * Client-side persistence for the transport `sessionId`
 * (BG-4.1 / `docs/cloud-vm/gui-parity-plan.md` GAP-4).
 *
 * GAP-4 of the GUI parity plan observed that the cookie-mode SPA's
 * `onSessionId` callback was a verbatim no-op TODO, so the SPA
 * constructed a FRESH `sessionId` on every boot — the server mints a
 * new one via `session.hello@1` because the client never replayed a
 * stashed id. That structurally prevents the GUI from ever driving
 * `session.resume@1` (Phase 4): you cannot resume a session whose id
 * you threw away on the previous tear-down. This module is the
 * persistence groundwork only — it stashes + reuses the id. It does
 * NOT implement resume itself (that is Phase 4, see §8.1 of
 * `agent-activity-journal-tdd.md`).
 *
 * Storage scope — DELIBERATE (see report + GAP-4):
 *
 *   - **`sessionStorage`, not `localStorage`.** The contract doc on
 *     `BrowserCrucibleClientOptions.onSessionId` already states the
 *     auth module "persists this into sessionStorage so a bfcache
 *     restore can resume." sessionStorage is per-tab and survives a
 *     *reload* but NOT a brand-new tab — which is exactly the desired
 *     session semantic: reloading the Workspace should resume the same
 *     transport session, but opening the app in a new tab is a new
 *     session. localStorage would leak one tab's session id into every
 *     other tab and outlive the browsing session entirely.
 *
 *   - **Global per tab, NOT keyed by gameId.** The `sessionId` here is
 *     the JSON-RPC *connection / transport* session (the pod sees it as
 *     `ctx.sessionId`, one per WebSocket — see
 *     `crucible/src/runtime/jsonrpc-methods.ts`), minted once by
 *     `session.hello@1` for the whole client, not per game. So a single
 *     global key is correct; a per-game key would be wrong here.
 *
 * Web-storage availability is guarded: Safari private mode (and some
 * locked-down embeddings) throw from `sessionStorage` access. On any
 * throw we fall back to an in-memory value so boot never crashes — the
 * id then lives only for this page's lifetime (no cross-reload resume,
 * but also no crash, matching pre-BG-4.1 behaviour).
 */

/**
 * sessionStorage key for the stashed transport sessionId. Namespaced
 * `crucible.cloud.*` to sit beside other Foundry SPA keys without
 * collision. (No existing crucible-web key convention to match — this
 * package had zero web-storage usages before BG-4.1.)
 */
export const SESSION_ID_STORAGE_KEY = 'crucible.cloud.sessionId';

/**
 * The wire `sessionId` is a UUID minted by the pod's `session.hello@1`.
 * We validate the stashed value's shape before replaying it so a
 * corrupted / tampered entry can't be fed back into `session.resume@1`
 * (Phase 4) as a bogus id — a malformed entry is treated as "no stash"
 * and a fresh `session.hello@1` mints a clean id. Case-insensitive,
 * RFC-4122-shaped.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** In-memory fallback when web storage is unavailable (private mode, etc.). */
let inMemorySessionId: string | null = null;

/**
 * Best-effort handle to `sessionStorage`. Accessing the property itself
 * can throw (not just its methods) in some sandboxes, so the probe is
 * wrapped. Returns null when unavailable → callers use the in-memory
 * fallback.
 */
function safeSessionStorage(): Storage | null {
  try {
    // `globalThis.sessionStorage` access throws in some sandboxed/SSR
    // contexts; the try/catch is load-bearing, not defensive padding.
    const storage = globalThis.sessionStorage;
    return storage ?? null;
  } catch {
    return null;
  }
}

/**
 * Read the stashed sessionId. Returns it only if present AND
 * UUID-shaped; otherwise null (caller mints fresh). Falls back to the
 * in-memory value if web storage is unavailable.
 */
export function readStoredSessionId(): string | null {
  const storage = safeSessionStorage();
  if (storage) {
    try {
      const value = storage.getItem(SESSION_ID_STORAGE_KEY);
      return value && UUID_RE.test(value) ? value : null;
    } catch {
      // Read threw mid-session (quota/security state change) — fall
      // through to the in-memory mirror below.
    }
  }
  return inMemorySessionId && UUID_RE.test(inMemorySessionId) ? inMemorySessionId : null;
}

/**
 * Persist (or clear) the sessionId. `onSessionId` calls this with the
 * minted/confirmed id, or with `''` after a hard session loss
 * (`BrowserCrucibleClient` clears via `onSessionId('')` on close /
 * resume-window-expiry / bfcache drain). An empty string removes the
 * stash so the next boot mints fresh rather than replaying a dead id.
 */
export function persistSessionId(id: string): void {
  // Always keep the in-memory mirror current so a later storage failure
  // still returns the right value within this page lifetime.
  inMemorySessionId = id ? id : null;
  const storage = safeSessionStorage();
  if (!storage) return;
  try {
    if (id) {
      storage.setItem(SESSION_ID_STORAGE_KEY, id);
    } else {
      storage.removeItem(SESSION_ID_STORAGE_KEY);
    }
  } catch {
    // Write threw (private-mode quota=0, etc.) — the in-memory mirror
    // above is the fallback; boot must not crash on a persistence miss.
  }
}

/** Reset module state. Test-only. */
export function __resetSessionIdStoreForTests(): void {
  inMemorySessionId = null;
}
